import { getState } from '$setup/redux'
import { disconnectionCallbackManager } from '$hooks/after/disconnection'
import { dateWithOffset } from '$logic/client-device/time-offset.service'

const UserWaitEventName = 'USER_WAIT'

export enum UserWaitPendingAction {
  WEB_SOCKET_CONNECTION = 'web-socket-connection',
  UPDATE_APP = 'update-app',
  LOAD_CHAT = 'load-chat',
  LOAD_MORE_MESSAGES = 'load-more-messages',
  UPLOAD = 'upload',
  JOIN_CALL = 'join-call',
  START_CALL = 'start-call',
  REJECT_INCOMING_CALL = 'reject-incoming-call',
  LOAD_SHARED_MEDIA = 'load-shared-media',
  DOWNLOAD = 'download',
}

const DEFAULT_MINIMUM_PENDING_DURATION = 0

export function reportUserWait(
  pendingAction: UserWaitPendingAction,
  duration: number,
  disconnectedDuringPending: boolean,
  additionalData?: Record<string, unknown>,
) {
  analite(UserWaitEventName, {
    pendingAction,
    duration,
    disconnectedDuringPending,
    ...additionalData,
  })
}

export function createUserWaitReporter(
  pendingAction: UserWaitPendingAction,
  minimumPendingDurationToReportMS = DEFAULT_MINIMUM_PENDING_DURATION,
  reportIfDisconnectedDuringPending = true,
  sendAnalyticsRequest = true,
  reportTimeoutMS?: number,
) {
  let startTime: number = -1
  let disconnectedDuringPending = false
  let timeoutData: Record<string, unknown> = {}
  let additionalData: Record<string, unknown> = {}
  let timeout: number | undefined
  let reportId = ''

  const onDisconnect = () => {
    disconnectedDuringPending = true
  }

  const addTimeoutData = (newData: Record<string, unknown>) => {
    timeoutData = { ...timeoutData, ...newData }
  }

  const onTimeout = (data?: Record<string, unknown>) => {
    if (startTime === -1) return false
    const duration = performance.now() - startTime
    if (sendAnalyticsRequest) {
      reportUserWait(pendingAction, duration, disconnectedDuringPending, {
        timeoutHappened: true,
        ...data,
        ...timeoutData,
        ...additionalData,
        reportId,
      })
    }
  }

  const addTimer = (count = 1) => {
    if (reportTimeoutMS) {
      timeout = window.setTimeout(() => {
        onTimeout({ timeoutMS: reportTimeoutMS, timeoutCount: count })
        addTimer(count + 1)
      }, reportTimeoutMS)
    }
  }

  const start = (data: Record<string, unknown>) => {
    reportId = `${pendingAction}-${dateWithOffset()}`
    window.clearTimeout(timeout)
    addTimer()
    additionalData = { ...data }
    disconnectionCallbackManager.removeCallback(onDisconnect)
    startTime = performance.now()
    const state = getState()
    if (
      state.view.main.connectionStatus !== 'connected' &&
      pendingAction !== UserWaitPendingAction.UPDATE_APP && // when app is loading, initial status of socket is never connected.
      pendingAction !== UserWaitPendingAction.WEB_SOCKET_CONNECTION
    ) {
      disconnectedDuringPending = true
    } else disconnectionCallbackManager.addCallback(onDisconnect)
  }

  const clear = () => {
    timeoutData = {}
    additionalData = {}
    clearTimeout(timeout)
    timeout = undefined
    startTime = -1
    disconnectionCallbackManager.removeCallback(onDisconnect)
    disconnectedDuringPending = false
  }

  const getDurationFromStart = () => {
    return performance.now() - startTime
  }

  const end = (
    data?: Record<string, unknown>,
  ): false | { duration: number; disconnectedDuringPending: boolean } => {
    if (startTime === -1) return false
    const duration = performance.now() - startTime

    if (
      minimumPendingDurationToReportMS < duration &&
      (reportIfDisconnectedDuringPending || !disconnectedDuringPending) &&
      sendAnalyticsRequest
    ) {
      reportUserWait(pendingAction, duration, disconnectedDuringPending, {
        ...data,
        ...additionalData,
        reportId,
      })
    }
    clear()
    return { duration, disconnectedDuringPending }
  }

  return { start, end, clear, getDurationFromStart, addTimeoutData }
}
